<template>
  <div class="modal-overlay" @click="$emit('close')">
    <div class="modal-container" :class="{ 'modal-container--splash': isSplashHelp }" @click.stop>
      <div class="modal-header" :class="{ 'modal-header--splash': isSplashHelp }">
        <button
          type="button"
          class="modal-close-button"
          @click="$emit('close')"
          aria-label="Close how it works"
        >
          ×
        </button>
        <h3 :class="{ 'modal-title--splash': isSplashHelp }">{{ isSplashHelp ? 'How to Use' : helpContent.title }}</h3>
      </div>

      <div class="modal-body" ref="modalContainerRef" :class="{ 'modal-body--splash': isSplashHelp }">
        <template v-if="isSplashHelp">
          <div class="splash-checklist-panel">
            <p class="splash-overview">{{ helpContent.overview }}</p>

            <ul v-if="splashChecklist.length > 0" class="splash-checklist">
              <li
                v-for="item in splashChecklist"
                :key="item.id"
                class="splash-checklist-item"
              >
                <span class="splash-checklist-icon" aria-hidden="true">
                  <img :src="item.iconUrl" alt="" class="splash-step-icon-image">
                </span>
                <span class="splash-checklist-text">{{ item.description }}</span>
              </li>
            </ul>
          </div>

          <div class="splash-cta">
            <button @click="$emit('close')" class="done-button splash-done-button">Get Started</button>
          </div>
        </template>

        <template v-else>
          <p class="overview">{{ helpContent.overview }}</p>

          <!-- Section Links (for splash/master modal) -->
          <div v-if="helpContent.sectionLinks && helpContent.sectionLinks.length > 0" class="section-links">
            <h4>Campaign sections</h4>
            <div class="links-grid">
              <div
                v-for="link in helpContent.sectionLinks"
                :key="link.sectionId"
                class="section-link-label"
              >
                <h5>{{ link.title }}</h5>
              </div>
            </div>
          </div>

          <!-- Tips -->
          <div v-if="helpContent.tips && helpContent.tips.length > 0" class="tips-section">
            <h4>Tips & best practices</h4>
            <ul class="tips-list">
              <li v-for="(tip, index) in helpContent.tips" :key="index">{{ tip }}</li>
            </ul>
          </div>

          <div v-if="isVoucherHelp" class="template-section">
            <h4>Voucher CSV template</h4>
            <p class="template-description">
              Download the required CSV template with the correct one-column header.
            </p>
            <button type="button" class="template-download-button" @click="downloadVoucherTemplateCsv">
              Download Template CSV
            </button>
          </div>

          <!-- Notes -->
          <div v-if="helpContent.notes" class="notes-section">
            <p class="note-text">{{ helpContent.notes }}</p>
          </div>
        </template>
      </div>

      <div v-if="!isSplashHelp" class="modal-footer">
        <button @click="$emit('close')" class="done-button">Done</button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, onMounted, onUnmounted, ref, type PropType } from 'vue';
import { helpContent as allHelpContent, type HelpContent } from '@/config/helpContent';
import { platformIcon } from '@/config/platformAssets';

export interface SplashChecklistItem {
  id: string;
  description: string;
  iconUrl: string;
}

export default defineComponent({
  name: 'HowItWorksModal',
  props: {
    currentPage: {
      type: String as PropType<string | null | undefined>,
      required: true,
    },
  },
  emits: ['close'],
  setup(props) {
    const modalContainerRef = ref<HTMLElement | null>(null);
    const currentPage = computed(() => props.currentPage || 'splash');

    const helpContent = computed<HelpContent>(() => {
      const page = currentPage.value;
      return allHelpContent[page] || {
        title: 'General Help',
        overview: 'This is the general help modal. It will provide context-specific information based on the section you are viewing.',
      };
    });

    const isSplashHelp = computed(() => currentPage.value === 'splash');
    const isVoucherHelp = computed(() => currentPage.value === 'voucher-config')

    const splashChecklist = computed<SplashChecklistItem[]>(() => [
      {
        id: 'play-area',
        description: 'Select where people can play: Everywhere or specific locations',
        iconUrl: platformIcon('star.png'),
      },
      {
        id: 'experience-type',
        description: 'Choose the type of experience: Quiz, AR etc',
        iconUrl: platformIcon('star.png'),
      },
      {
        id: 'look-and-feel',
        description: 'Customise your look by adding your colours, images, messages and objects',
        iconUrl: platformIcon('star.png'),
      },
      {
        id: 'experience-config',
        description: 'Customise the experience (quiz, AR etc)',
        iconUrl: platformIcon('star.png'),
      },
      {
        id: 'rewards',
        description: 'Add or create your prizes, rewards or vouchers',
        iconUrl: platformIcon('star.png'),
      },
      {
        id: 'schedule',
        description: 'Schedule your start and end dates/times',
        iconUrl: platformIcon('star.png'),
      },
      {
        id: 'register',
        description: 'Register your campaign for future use',
        iconUrl: platformIcon('star.png'),
      },
      {
        id: 'publish',
        description: 'Publish your campaign and we will then send you a bespoke URL link via email that you can share across your marketing communications',
        iconUrl: platformIcon('star.png'),
      },
    ]);

    const downloadVoucherTemplateCsv = () => {
      const blob = new Blob(['Vouchers\nABC12345\nXYZ98765\n'], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = 'voucher-template.csv'
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)
      URL.revokeObjectURL(url)
    }


    // Handle scroll events to prevent background scrolling
    const handleWheel = (event: WheelEvent) => {
      const target = event.target as HTMLElement;
      const modalContainer = modalContainerRef.value;
      
      // If the scroll is happening inside the modal container, handle it
      if (modalContainer && (target === modalContainer || modalContainer.contains(target))) {
        // Check if we're at the boundaries of the modal
        const { scrollTop, scrollHeight, clientHeight } = modalContainer;
        const isAtTop = scrollTop <= 1; // Small threshold for rounding
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;
        
        // If scrolling up at top or scrolling down at bottom, prevent default
        // to stop the scroll from propagating to the background
        if ((event.deltaY < 0 && isAtTop) || (event.deltaY > 0 && isAtBottom)) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        // Otherwise, allow the modal to scroll naturally (don't prevent default)
        // but stop propagation so FormView doesn't also handle it
        event.stopPropagation();
        return;
      }
      
      // If scroll is happening on the overlay (outside modal container),
      // prevent it from reaching the background
      event.preventDefault();
      event.stopPropagation();
    };

    // Handle touch scroll events for mobile
    const handleTouchMove = (event: TouchEvent) => {
      const target = event.target as HTMLElement;
      const modalContainer = modalContainerRef.value;
      
      // Allow touch scrolling inside the modal container
      if (modalContainer && (target === modalContainer || modalContainer.contains(target))) {
        return;
      }
      
      // Prevent touch scrolling on the overlay
      event.preventDefault();
    };

    onMounted(() => {
      // Add event listeners to capture scroll events
      window.addEventListener('wheel', handleWheel, { passive: false, capture: true });
      window.addEventListener('touchmove', handleTouchMove, { passive: false, capture: true });
    });

    onUnmounted(() => {
      // Clean up event listeners
      window.removeEventListener('wheel', handleWheel, { capture: true } as EventListenerOptions);
      window.removeEventListener('touchmove', handleTouchMove, { capture: true } as EventListenerOptions);
    });

    return {
      helpContent,
      isSplashHelp,
      isVoucherHelp,
      splashChecklist,
      downloadVoucherTemplateCsv,
      modalContainerRef,
    };
  },
});
</script>

<style scoped>
.modal-overlay {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-container--splash {
  background: linear-gradient(180deg, #121936 0%, #0f1731 100%) !important;
  border-color: rgba(142, 246, 198, 0.38) !important;
  color: #ffffff !important;
  --ui-inverse-text: #ffffff;
  --color-heading: #ffffff;
  --ui-inverse-cta-bg: var(--vt-c-accent);
  --ui-inverse-cta-bg-hover: #8ef6c6;
  --ui-inverse-cta-text: var(--color-background);
  max-width: min(26rem, 100%) !important;
}

.modal-overlay .modal-container:not(.modal-container--splash) {
  background: var(--ui-inverse-surface) !important;
  --ui-inverse-text: #ffffff;
  --ui-inverse-text-muted: rgba(236, 255, 247, 0.88);
  --ui-inverse-border: rgba(255, 255, 255, 0.18);
  --ui-inverse-cta-bg: var(--vt-c-accent);
  --ui-inverse-cta-bg-hover: #8ef6c6;
  --ui-inverse-cta-text: var(--color-background);
}

.modal-overlay .modal-container:not(.modal-container--splash) .modal-header,
.modal-overlay .modal-container:not(.modal-container--splash) .modal-body,
.modal-overlay .modal-container:not(.modal-container--splash) .modal-footer {
  background: var(--ui-inverse-surface) !important;
}

 .modal-header--splash {
  border-bottom: 1px solid rgba(142, 246, 198, 0.24);
  justify-content: flex-start;
  background: var(--ui-inverse-surface);
  color: #ffffff;
}

.modal-header--splash h3 {
  margin-right: 1.5rem;
  color: inherit;
  text-align: left;
  font-size: clamp(1.5rem, 4.8vw, 1.9rem);
}

.modal-header--splash h3 {
  margin: 0;
  margin-right: 1.5rem;
  color: #ffffff !important;
  text-align: center;
  font-size: clamp(1.5rem, 4.8vw, 1.9rem);
}

.modal-title--splash {
  display: block !important;
  width: 100%;
  position: relative;
  z-index: 2;
  color: #ffffff !important;
  text-align: center !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
}

h3.modal-title--splash {
  color: #ffffff !important;
}

.modal-header--splash .modal-close-button {
  color: #ecfff6;
}

 .modal-body--splash {
  padding: 1.1rem 1.15rem 1.35rem;
  background: var(--ui-inverse-surface);
  color: #ffffff;
}

.modal-footer {
  padding: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  justify-content: center;
  align-items: center;
  background: var(--ui-inverse-surface);
}

.modal-footer .done-button {
  background: var(--vt-c-accent) !important;
  color: var(--color-background) !important;
  border-color: var(--vt-c-accent) !important;
  border-radius: 999px !important;
  padding: 0.85rem 1.75rem !important;
  font-weight: 800 !important;
  box-shadow: 0 10px 25px rgba(7, 10, 20, 0.25) !important;
}

.splash-checklist-panel {
  background: var(--ui-inverse-surface);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.95rem;
  padding: 0.95rem 0.95rem 1rem;
  box-shadow:
    0 0.45rem 1.2rem rgba(7, 10, 20, 0.22),
    0 0 0 1px rgba(255, 255, 255, 0.04) inset;
}

.modal-container--splash p.splash-overview {
  margin: 0 0 1.1rem 0;
  color: rgba(236, 255, 247, 0.92) !important;
  line-height: 1.4;
  font-size: 0.86rem;
  text-align: left;
}

.splash-checklist {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  text-align: left;
}

.splash-checklist-item {
  display: grid;
  grid-template-columns: 1.2rem minmax(0, 1fr);
  align-items: start;
  gap: 0.65rem;
  text-align: left;
}

.splash-checklist-icon {
  width: 1.2rem;
  height: 1.2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.05rem;
}

.splash-step-icon-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.splash-checklist-text {
  display: block;
  font-size: 0.78rem;
  line-height: 1.35;
  font-weight: 600;
  color: rgba(236, 255, 247, 0.94);
  text-align: left;
}

.splash-cta {
  margin-top: 0.95rem;
  padding-top: 0.85rem;
  border-top: 1px solid rgba(142, 246, 198, 0.2);
}

.splash-done-button {
  background: var(--vt-c-accent) !important;
  color: var(--color-background) !important;
  border-color: var(--vt-c-accent) !important;
  font-size: 0.95rem;
  font-weight: 800;
}

.overview {
  font-size: 1.1rem;
  color: var(--ui-inverse-text-muted);
  opacity: 1;
  line-height: 1.6;
  margin-bottom: 2rem;
  text-align: center;
  font-family: var(--ui-inverse-font-family);
}

h4 {
  font-size: 1.3rem;
  font-weight: 800;
  color: var(--ui-inverse-text);
  margin-top: 2rem;
  margin-bottom: 1rem;
  font-family: var(--ui-inverse-font-family);
}

h5 {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--ui-inverse-text);
  margin-bottom: 0.5rem;
  font-family: var(--ui-inverse-font-family);
}

.done-button {
  width: 100%;
}

/* Section Links (for splash modal) */
.section-links {
  margin-top: 2rem;
}

.links-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.section-link-label {
  padding: 0.75rem 1rem;
  text-align: center;
}

.section-link-label h5 {
  margin: 0;
  color: var(--ui-inverse-text);
  font-size: 1rem;
  font-weight: 700;
}

/* Steps Section */
.steps-section {
  margin-top: 2rem;
}

.steps-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.step-item {
  display: flex;
  align-items: flex-start;
  gap: 1.25rem;
  padding: 1.25rem;
  background-color: rgba(16, 26, 52, 0.08);
  border: 1px solid var(--ui-inverse-border);
  border-radius: 1rem;
  transition: all 0.3s ease;
}

.step-item:hover {
  border-color: var(--ui-inverse-border-strong);
  box-shadow: none;
  transform: none;
}

.step-number {
  flex-shrink: 0;
  width: 2.5rem;
  height: 2.5rem;
  background: var(--ui-inverse-cta-bg);
  color: var(--ui-inverse-cta-text);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.1rem;
  box-shadow: none;
  font-family: var(--ui-inverse-font-family);
}

.step-content {
  flex: 1;
}

.step-content h5 {
  margin-top: 0;
  margin-bottom: 0.5rem;
}

.step-content p {
  margin: 0;
  color: var(--ui-inverse-text-muted);
  opacity: 1;
  line-height: 1.5;
  font-family: var(--ui-inverse-font-family);
}

/* Tips Section */
.tips-section {
  margin-top: 2rem;
  padding: 1.25rem;
  background-color: rgba(16, 26, 52, 0.08);
  border-left: 0.25rem solid var(--ui-inverse-text);
  border-radius: 0.5rem;
}

.tips-list {
  list-style: none;
  padding: 0;
  margin: 0.5rem 0 0 0;
}

.tips-list li {
  padding: 0.5rem 0;
  padding-left: 1.5rem;
  position: relative;
  color: var(--ui-inverse-text-muted);
  opacity: 1;
  line-height: 1.6;
  font-family: var(--ui-inverse-font-family);
}

.tips-list li::before {
  content: '💡';
  position: absolute;
  left: 0;
  top: 0.5rem;
}

.template-section {
  margin-top: 2rem;
  padding: 1.25rem;
  background-color: rgba(16, 26, 52, 0.08);
  border: 1px solid var(--ui-inverse-border);
  border-radius: 0.75rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
}

.template-description {
  margin: 0;
  color: var(--ui-inverse-text-muted);
  opacity: 1;
  line-height: 1.5;
  text-align: left;
  font-family: var(--ui-inverse-font-family);
}

.template-download-button {
  margin: 0;
}

/* Notes Section */
.notes-section {
  margin-top: 2rem;
  padding: 1rem;
  background-color: rgba(16, 26, 52, 0.08);
  border-radius: 0.5rem;
  border: 1px solid var(--ui-inverse-border);
}

.note-text {
  margin: 0;
  color: var(--ui-inverse-text-muted);
  opacity: 1;
  font-style: italic;
  line-height: 1.6;
  font-family: var(--ui-inverse-font-family);
}

@media (max-width: 64rem) {
  .modal-header--splash {
    padding: 1.1rem 3rem 1rem 1.1rem;
  }

  .modal-header--splash h3 {
    margin-right: 0.5rem;
  }

  .modal-body--splash {
    padding: 0.95rem 0.95rem 1.2rem;
  }

  .links-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 24rem) {
  .splash-checklist-item {
    grid-template-columns: 1rem minmax(0, 1fr);
    gap: 0.55rem;
  }

  .splash-checklist-icon {
    width: 1rem;
    height: 1rem;
  }
}
</style>
